// src/services/activityLogService.js
// Unified activity logging service for Equity and Dataroom modules

import { supabase } from '../config';

const MODULES = {
  EQUITY: 'equity',
  DATAROOM: 'dataroom',
  CAPRAISE: 'capraise'
};

const ACTION_TYPES = {
  // Auth actions
  LOGIN: 'login',
  LOGOUT: 'logout',
  
  // Equity actions
  COMPANY_ACCESSED: 'company_accessed',
  COMPANY_VIEWED: 'company_viewed',
  PAGE_ACCESSED: 'page_accessed',
  REPORT_GENERATED: 'report_generated',
  EXPORT_DOWNLOADED: 'export_downloaded',
  ISSUANCE_CREATED: 'issuance_created',
  ISSUANCE_UPDATED: 'issuance_updated',
  ISSUANCE_DELETED: 'issuance_deleted',
  COMPANY_CREATED: 'company_created',
  COMPANY_UPDATED: 'company_updated',
  COMPANY_DELETED: 'company_deleted',
  SHAREHOLDER_CREATED: 'shareholder_created',
  SHAREHOLDER_UPDATED: 'shareholder_updated',
  SHAREHOLDER_DELETED: 'shareholder_deleted',
  DIRECTOR_CREATED: 'director_created',
  DIRECTOR_UPDATED: 'director_updated',
  DIRECTOR_DELETED: 'director_deleted',
  TRANSACTION_CREATED: 'transaction_created',
  
  // Dataroom actions
  PROJECT_ACCESSED: 'project_accessed',
  FOLDER_ACCESSED: 'folder_accessed',
  FILE_ACCESSED: 'file_accessed',
  FILE_DOWNLOADED: 'file_downloaded',
  FILE_UPLOADED: 'file_uploaded',
  FILE_PREVIEWED: 'file_previewed'
};

class ActivityLogService {
  /**
   * Get client metadata (IP, user agent, session)
   */
  getClientMetadata() {
    return {
      user_agent: navigator.userAgent,
      session_id: this.getSessionId(),
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Get or create session ID
   */
  getSessionId() {
    let sessionId = sessionStorage.getItem('activity_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('activity_session_id', sessionId);
    }
    return sessionId;
  }

  /**
   * Log activity for Equity module
   */
  async logEquityActivity(action, metadata = {}) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.warn('Cannot log activity: User not authenticated');
        return { success: false, error: 'User not authenticated' };
      }

      const clientMetadata = this.getClientMetadata();
      
      // Try to insert into equity_logs table
      const { data, error } = await supabase
        .from('equity_logs')
        .insert({
          user_id: user.id,
          action: action,
          company_id: metadata.company_id || null,
          details: {
            ...metadata,
            ...clientMetadata
          }
        })
        .select()
        .single();

      if (error) {
        // If equity_logs table doesn't exist, log to console
        console.warn('equity_logs table not found, logging to console:', {
          user_id: user.id,
          action,
          metadata: { ...metadata, ...clientMetadata }
        });
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error logging equity activity:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Log activity for Dataroom module
   */
  async logDataroomActivity(projectId, action, metadata = {}) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.warn('Cannot log activity: User not authenticated');
        return { success: false, error: 'User not authenticated' };
      }

      const clientMetadata = this.getClientMetadata();

      const { data, error } = await supabase
        .from('dataroom_logs')
        .insert({
          user_id: user.id,
          project_id: projectId,
          action: action,
          details: {
            ...metadata,
            ...clientMetadata
          }
        })
        .select()
        .single();

      if (error) {
        console.error('Error logging dataroom activity:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error logging dataroom activity:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Log user login
   */
  async logLogin(product = null) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { success: false, error: 'User not authenticated' };

      const clientMetadata = this.getClientMetadata();
      const metadata = {
        product: product || 'general',
        login_method: 'password',
        ...clientMetadata
      };

      // Log to both equity and dataroom if applicable, or create a unified login log
      // For now, we'll log to equity_logs for general login tracking
      const result = await this.logEquityActivity(ACTION_TYPES.LOGIN, metadata);
      
      return result;
    } catch (error) {
      console.error('Error logging login:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Log user logout
   */
  async logLogout() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { success: false, error: 'User not authenticated' };

      const metadata = {
        ...this.getClientMetadata()
      };

      const result = await this.logEquityActivity(ACTION_TYPES.LOGOUT, metadata);
      
      // Clear session ID on logout
      sessionStorage.removeItem('activity_session_id');
      
      return result;
    } catch (error) {
      console.error('Error logging logout:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Log Equity company access
   */
  async logCompanyAccess(companyId, companyName) {
    return await this.logEquityActivity(ACTION_TYPES.COMPANY_ACCESSED, {
      company_id: companyId,
      company_name: companyName
    });
  }

  /**
   * Log Equity page access
   */
  async logPageAccess(pageName, companyId = null) {
    return await this.logEquityActivity(ACTION_TYPES.PAGE_ACCESSED, {
      page: pageName,
      company_id: companyId
    });
  }

  /**
   * Log Dataroom folder access
   */
  async logFolderAccess(projectId, folderId, folderName) {
    return await this.logDataroomActivity(projectId, ACTION_TYPES.FOLDER_ACCESSED, {
      folder_id: folderId,
      folder_name: folderName
    });
  }

  /**
   * Log Dataroom file access
   */
  async logFileAccess(projectId, fileId, fileName, actionType = ACTION_TYPES.FILE_ACCESSED) {
    return await this.logDataroomActivity(projectId, actionType, {
      file_id: fileId,
      file_name: fileName
    });
  }

  /**
   * Get activity logs for Equity
   */
  async getEquityLogs(userId = null, limit = 100) {
    try {
      let query = supabase
        .from('equity_logs')
        .select('*')
        .order('timestamp', { ascending: false })
        .limit(limit);

      if (userId) {
        query = query.eq('user_id', userId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching equity logs:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  /**
   * Get activity logs for Dataroom
   */
  async getDataroomLogs(projectId, limit = 100) {
    try {
      const { data, error } = await supabase
        .from('dataroom_logs')
        .select('*')
        .eq('project_id', projectId)
        .order('timestamp', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Error fetching dataroom logs:', error);
      return { success: false, error: error.message, data: [] };
    }
  }
}

// Export singleton instance
const activityLogService = new ActivityLogService();
export default activityLogService;

// Export constants
export { MODULES, ACTION_TYPES };

